import { formatShort } from '@/utils/datetime';
// AI Learning Sources — control panel for both crawlers:
//   1) App-data crawler   — indexes Sensbey Mongo collections
//   2) NAS crawler        — indexes files on the on-premises NAS
// Both use the same pausable/resumable worker infra and the same
// `search_index` collection, so search results are unified.

import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Play, Pause, StopCircle, CircleNotch, CheckCircle, Warning, ArrowsClockwise,
  MagnifyingGlass, Brain, Database, HardDrives, Plus, X, FloppyDisk,
  Folder, File as FileIcon, House, CaretRight, ShieldCheck, ListMagnifyingGlass,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EvidenceRouterCard } from '@/pages/settings/EvidenceRouterSettings';
import { DangerZone, DangerAction } from '@/components/DangerZone';
import { NasRunDetailSheet } from '@/components/nas/NasRunDetailSheet';
import { NasPreviewProgressCard } from '@/components/nas/NasPreviewProgressCard';
import { NasPreviewModePanel } from '@/components/nas/NasPreviewModePanel';
import { NasIngestProgressPanel } from '@/components/nas/NasIngestProgressPanel';
import { NasIngestDetailSheet } from '@/components/nas/NasIngestDetailSheet';
import { derivePreviewState, deriveCrawlState } from '@/components/nas/nasJobState';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  running:   { color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200', label: '実行中', Icon: CircleNotch, spin: true },
  paused:    { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: '一時停止中', Icon: Pause },
  complete:  { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: '完了', Icon: CheckCircle },
  cancelled: { color: 'text-zinc-600', bg: 'bg-zinc-50', border: 'border-zinc-200', label: '中止', Icon: StopCircle },
  failed:    { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', label: '失敗', Icon: Warning },
};

const fmtDT = (iso) => (iso ? formatShort(iso) : '—');

const fmtElapsed = (fromIso, toIso) => {
  if (!fromIso) return '—';
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((to - from) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
};

const authHeader = () => {
  const tok = localStorage.getItem('token');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};


const PREFLIGHT_STATUS_LABEL = {
  succeeded: '成功', failed: '失敗', not_started: '未実施',
  not_required: '不要（標準モード）',
};

const LEGACY_404_RE = /指定されたパスが見つかりません|path not found/i;


// ── Reusable job status card ───────────────────────────────────────────
function JobStatusCard({ job, testId, state }) {
  if (!job) return null;
  const st = state || STATUS_STYLE[job.status] || STATUS_STYLE.paused;
  const label = st.labelJa || st.label;
  const total = job.current_module_total || 0;
  const done = job.current_module_done || 0;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className={`rounded-lg border ${st.border} ${st.bg} px-4 py-3.5`} data-testid={testId}
      data-job-state={st.key || job.status}>
      <div className="flex flex-wrap items-center gap-3">
        <st.Icon size={18} className={`${st.color} shrink-0 ${st.spin ? 'animate-spin' : ''}`} />
        <p className={`text-[13px] font-semibold ${st.color} flex-1 min-w-[180px]`}>
          {label} — {job.progress_label || '—'}
          {st.labelEn && <span className="block text-[10.5px] font-normal opacity-70">{st.labelEn}</span>}
        </p>
        <div className={`text-[11px] font-semibold ${st.color} shrink-0`}>
          経過 <span className="tabular-nums">{fmtElapsed(job.started_at, job.finished_at)}</span>
        </div>
      </div>
      {total > 0 && (
        <>
          <div className="mt-3 h-2 rounded-full bg-white/70 border border-zinc-200 overflow-hidden">
            <div className="h-full bg-teal-700 transition-all"
              style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-zinc-500 mt-1.5">
            {job.current_module}: {done} / {total} 件 ({pct}%)
          </p>
        </>
      )}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]">
        {['indexed', 'embedded', 'unchanged', 'skipped', 'failed'].map((k) => (
          <div key={k} className="rounded bg-white/70 border border-zinc-200 px-2 py-1.5"
            data-testid={`crawler-stat-${k}`}>
            <p className="text-zinc-500">{ {indexed: 'インデックス化', embedded: '意味検索の準備', unchanged: '変更なし', skipped: 'スキップ', failed: '失敗'}[k] }</p>
            <p className={`font-bold tabular-nums ${k==='failed'?'text-rose-700':k==='indexed'?'text-emerald-700':k==='embedded'?'text-teal-700':'text-zinc-700'}`}>
              {job.stats?.[k] || 0}
            </p>
          </div>
        ))}
      </div>
      {job.error && (
        <div className="mt-2">
          <p className="text-[11px] text-rose-700 whitespace-pre-wrap">エラー: {job.error}</p>
          {LEGACY_404_RE.test(job.error) && (
            <p className="text-[10.5px] text-amber-700 mt-0.5" data-testid="job-legacy-diagnostic-note">
              旧診断形式: NASエージェントのAPI不足による可能性があります
              <span className="block opacity-80">Legacy diagnostic format: this may have been caused by a missing NAS-agent endpoint.</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}


// ── App-data crawler card ──────────────────────────────────────────────
function AppDataCrawler({ onStatsChanged }) {
  const [job, setJob] = useState(null);
  const [crawlerCfg, setCrawlerCfg] = useState({});
  const [events, setEvents] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = async () => {
    try {
      const r = await axios.get(`${API}/api/crawler/jobs`, {
        params: { kind: 'app_data', limit: 10 }, headers: authHeader() });
      setHistory(r.data || []);
      const active = (r.data || []).find((j) => ['running', 'paused'].includes(j.status))
                     || (r.data || [])[0] || null;
      setJob(active);
    } catch { /* silent */ }
  };

  useEffect(() => { loadJobs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (job?.status === 'running') {
      pollRef.current = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/api/crawler/${job.id}`, { headers: authHeader() });
          setJob(r.data);
          if (r.data.status !== 'running') { onStatsChanged?.(); clearInterval(pollRef.current); }
        } catch { /* transient */ }
      }, 2000);
      return () => clearInterval(pollRef.current);
    }
  }, [job?.id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/crawler/app-data/start`, {}, { headers: authHeader() });
      const j = await axios.get(`${API}/api/crawler/${r.data.id}`, { headers: authHeader() });
      setJob(j.data);
      toast.success('アプリデータのクロールを開始しました');
      loadJobs();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === 'object' ? d.message : d) || 'クロールの開始に失敗しました');
    }
    finally { setBusy(false); }
  };
  const doAction = async (path) => {
    if (!job) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/crawler/${job.id}/${path}`, {}, { headers: authHeader() });
      const j = await axios.get(`${API}/api/crawler/${job.id}`, { headers: authHeader() });
      setJob(j.data); loadJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || `${path} に失敗しました`); }
    finally { setBusy(false); }
  };

  const canStart = !job || ['complete', 'cancelled', 'failed'].includes(job.status);
  const canPause = job?.status === 'running';
  const canResume = job?.status === 'paused';
  const canCancel = ['running', 'paused'].includes(job?.status);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="ai-learning-appdata-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-700 flex items-center justify-center shrink-0">
          <Database size={18} weight="duotone" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-zinc-900">Web アプリのデータ</h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">技術日報 / ナレッジ / お知らせ / プロジェクト / IT チケット / ダイジェスト を横断インデックス化</p>
        </div>
      </div>
      <div className="px-5 py-4 space-y-3">
        {job ? <JobStatusCard job={job} testId="appdata-job" /> : (
          <div className="rounded-lg border border-dashed border-zinc-200 px-4 py-6 text-center">
            <Database size={22} className="mx-auto text-zinc-400" />
            <p className="text-[13px] text-zinc-500 mt-1.5">まだクロールが実行されていません</p>
            <p className="text-[11px] text-zinc-400">「今すぐクロール」を押して開始します（1〜数分）</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={start} disabled={busy || !canStart}
            className="h-9 bg-teal-700 hover:bg-teal-800 text-[13px]" data-testid="appdata-start-btn">
            <Play size={13} weight="fill" className="mr-1" /> 今すぐクロール
          </Button>
          {canPause && <Button onClick={() => doAction('pause')} disabled={busy} variant="outline" className="h-9 text-[13px]" data-testid="appdata-pause-btn"><Pause size={13} weight="fill" className="mr-1" />一時停止</Button>}
          {canResume && <Button onClick={() => doAction('resume')} disabled={busy} className="h-9 bg-teal-700 hover:bg-teal-800 text-[13px]" data-testid="appdata-resume-btn"><Play size={13} weight="fill" className="mr-1" />再開</Button>}
          {canCancel && <Button onClick={() => doAction('cancel')} disabled={busy} variant="outline" className="h-9 text-[13px] border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="appdata-cancel-btn"><StopCircle size={13} className="mr-1" />中止</Button>}
        </div>
      </div>
      {history.length > 1 && (
        <div className="px-5 py-3 border-t border-zinc-200">
          <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">履歴</p>
          <div className="divide-y divide-zinc-200 border border-zinc-200 rounded text-[11px]" data-testid="appdata-history">
            {history.slice(0, 5).map((h) => {
              const s = STATUS_STYLE[h.status] || STATUS_STYLE.paused;
              return (
                <div key={h.id} className="px-3 py-1.5 flex items-center gap-2">
                  <s.Icon size={12} className={s.color} />
                  <span className={`font-semibold ${s.color} shrink-0`}>{s.label}</span>
                  <span className="text-zinc-500 tabular-nums shrink-0">{fmtDT(h.started_at)}</span>
                  <span className="text-zinc-400 truncate flex-1">索引 {h.stats?.indexed || 0} / スキップ {h.stats?.skipped || 0}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ── NAS ブラウザ — file-manager style drill-down of the NAS ────────────
// Calls GET /api/crawler/nas/browse?prefix= to get one level of children,
// aggregated by first path segment. Every folder row has "このフォルダー
// をクローラーに追加" so admins can seed the crawl from what they can see.
// 状態は「読み込み中 / 空 / 未接続 / 認証エラー / タイムアウト / サーバーエラー」を
// 明確に区別する（API失敗を「空フォルダー」として見せない）。
// クローラー経路の表示名。互換モードを「ローカル NAS」と偽って表示しない。
const TRANSPORT_LABEL = {
  local: 'NAS エージェント（NAS_CRAWLER_URL）',
  'legacy-storage-endpoint': '互換モード（旧ストレージURL）',
  fallback: 'フォールバック（公開URL）',
};

const BROWSE_STATE_LABEL = {
  authentication_failed: 'NAS エージェントの認証に失敗しました（トークンを確認してください）',
  forbidden: 'NAS エージェントからアクセスを拒否されました',
  timeout: 'NAS エージェントが時間内に応答しませんでした',
  connection_refused: 'NAS エージェントに接続できません（未起動・ポート未公開）',
  dns_failure: 'NAS エージェントのホスト名を解決できませんでした',
  not_configured: 'NAS クローラーの接続先が未設定です（NAS_CRAWLER_URL）',
  listing_unsupported: 'この NAS エージェントはフォルダー一覧に未対応です（v1.3.0 以降に更新してください）',
  not_found: '指定されたフォルダーが見つかりません',
  invalid_path: 'パスの形式が不正です',
  invalid_response: 'NAS エージェントの応答が不正です',
  server_error: 'NAS エージェントでエラーが発生しました',
};

function NasBrowser({ onAddPath }) {
  // Beta-0.5A: displayPath は表示専用（"/" を表示）、requestPath はエージェント
  // に送る「相対パス」（root は必ず ""）。 "/", "-", "undefined", "null",
  // "/crawl" は絶対に送らない。
  const [requestPath, setRequestPath] = useState('');
  const [data, setData] = useState(null);   // {folders, files, ...}
  const [loading, setLoading] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const load = useMemo(() => (p, hidden) => {
    setLoading(true);
    // ブラウザは常に Sensbey バックエンドを呼ぶ。NAS へは必ずサーバー側から接続する。
    // 相対パス（root は空文字列）だけを送る。エンコードは axios params に任せる。
    axios.get(`${API}/api/crawler/nas/browse`, {
      params: { prefix: p, limit: 100, show_hidden: hidden ? 'true' : 'false' },
      headers: authHeader(),
    }).then((r) => {
      const body = r.data;
      // Beta-0.5A: レスポンス契約の厳格チェック。空を静かに扱わない。
      if (!body || body.ok !== true) {
        setData({ folders: [], files: [], total_folders: 0, total_files: 0,
                  failed: true, category: 'invalid_response',
                  error: 'NASクローラーの応答形式が無効です。' });
        return;
      }
      if (!Array.isArray(body.folders) || !Array.isArray(body.files)) {
        setData({ folders: [], files: [], total_folders: 0, total_files: 0,
                  failed: true, category: 'invalid_response',
                  error: 'NASクローラーの応答形式が無効です（フォルダー・ファイル配列がありません）。' });
        return;
      }
      setData(body);
    })
      .catch((e) => {
        const d = e?.response?.data?.detail;
        const category = (d && typeof d === 'object' && d.category) || 'server_error';
        setData({ folders: [], files: [], total_folders: 0, total_files: 0,
                  failed: true, category,
                  error: BROWSE_STATE_LABEL[category]
                    || (typeof d === 'string' ? d : 'NAS 一覧の取得に失敗しました') });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(requestPath, showHidden); }, [requestPath, showHidden, load]);

  // パンくずは requestPath から組み立てる。displayPath は "/" 固定表示。
  const crumbs = useMemo(() => {
    const parts = requestPath.replace(/\/$/, '').split('/').filter(Boolean);
    const acc = [];
    let cur = '';
    parts.forEach((seg) => { cur = cur ? `${cur}/${seg}` : seg; acc.push({ name: seg, path: cur }); });
    return acc;
  }, [requestPath]);

  // 子フォルダーを開く: エージェントが返した item.path をそのまま使う。
  // 手で "/" を連結しない。空・null・"/" は絶対に送らない。
  const openFolder = (folder) => {
    const p = (folder?.path || '').replace(/^\/+/, '').replace(/\/+$/, '');
    if (!p || p === '.' || p === '..') return;
    setRequestPath(p);
  };

  const goHome = () => setRequestPath('');

  const emptyButValid = !loading && data && !data.failed
    && (data.folders?.length || 0) === 0
    && (data.files?.length || 0) === 0;

  const onlyHiddenPresent = emptyButValid && !showHidden
    && ((data.hidden_folder_count || 0) > 0 || (data.hidden_file_count || 0) > 0);

  return (
    <div className="rounded-lg border border-zinc-200" data-testid="nas-browser">
      <div className="px-3 py-2 border-b border-zinc-200 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-[12px] font-semibold text-zinc-700">
          <HardDrives size={13} className="text-amber-500" /> NAS ブラウザ
          <span className="ml-1 font-normal text-[10.5px] px-1.5 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-600"
            data-testid="nas-browser-scope-badge">スコープ設定用（承認範囲外も表示）</span>
        </div>
        <div className="flex-1 min-w-[200px] flex items-center gap-1 flex-wrap text-[12px] font-mono">
          <button type="button" onClick={goHome}
            data-testid="nas-browser-crumb-root"
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-zinc-100 text-zinc-600">
            <House size={11} /> /
          </button>
          {crumbs.map((c, i) => (
            <span key={c.path} className="inline-flex items-center gap-0.5">
              <CaretRight size={10} className="text-zinc-400" />
              <button type="button" onClick={() => setRequestPath(c.path)}
                data-testid={`nas-browser-crumb-${i}`}
                className="px-1.5 py-0.5 rounded hover:bg-zinc-100 text-zinc-700">{c.name}</button>
            </span>
          ))}
        </div>
        <label className="inline-flex items-center gap-1 text-[11px] text-zinc-600 cursor-pointer"
          data-testid="nas-browser-show-hidden-label">
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)}
            data-testid="nas-browser-show-hidden" className="accent-zinc-600" />
          非表示項目を表示
        </label>
        <Button size="sm" variant="outline" onClick={() => load(requestPath, showHidden)}
          className="h-7 text-[11px]" data-testid="nas-browser-refresh">
          <ArrowsClockwise size={11} className="mr-1" /> 更新
        </Button>
        {requestPath && (
          <Button size="sm" onClick={() => onAddPath(requestPath, crumbs[crumbs.length - 1]?.name || '')}
            className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700"
            data-testid="nas-browser-add-current">
            <Plus size={11} className="mr-1" />このパスを追加
          </Button>
        )}
      </div>

      {loading && (
        <p className="px-3 py-6 text-center text-[12px] text-zinc-400" data-testid="nas-browser-loading">
          <CircleNotch size={14} className="inline animate-spin mr-1" /> 読み込み中…
        </p>
      )}

      {!loading && data?.failed && (
        <div className="px-3 py-6 text-center" data-testid="nas-browser-error"
          data-category={data.category}>
          <p className="text-[12px] font-semibold text-rose-700">{data.error}</p>
          <p className="text-[11px] text-zinc-500 mt-1">
            接続状態は上部のバッジで確認できます。フォルダーが空だったわけではありません。
          </p>
        </div>
      )}

      {onlyHiddenPresent && (
        <p className="px-3 py-6 text-center text-[12px] text-zinc-500" data-testid="nas-browser-only-hidden">
          このフォルダーには非表示のシステム項目のみがあります。
        </p>
      )}

      {!loading && data && !data.failed && emptyButValid && !onlyHiddenPresent && (
        <p className="px-3 py-6 text-center text-[12px] text-zinc-400" data-testid="nas-browser-empty">
          このフォルダーは空です（接続は正常です）
        </p>
      )}

      {!loading && data && !data.failed && (data.folders?.length > 0 || data.files?.length > 0) && (
        <>
          {data.folders?.length > 0 && (
            <div>
              {data.folders.map((f) => (
                <div key={f.path} className="px-3 py-1.5 border-t border-zinc-200 flex items-center gap-2 hover:bg-amber-50/40"
                  data-testid={`nas-browser-folder-${f.name}`}>
                  <button type="button" onClick={() => openFolder(f)}
                    className="flex-1 min-w-0 flex items-center gap-2 text-left"
                    data-testid={`nas-browser-open-${f.name}`}>
                    <Folder size={14} weight="fill" className="text-amber-500 shrink-0" />
                    <span className="text-[12px] font-mono text-zinc-800 truncate">{f.name}/</span>
                    <span className="text-[11px] text-zinc-400 shrink-0 tabular-nums">{f.file_count ? `${f.file_count} 件` : ''}{f.total_bytes ? ` · ${fmtBytes(f.total_bytes)}` : ''}</span>
                  </button>
                  <button type="button" onClick={() => onAddPath(f.path.replace(/\/+$/, ''), f.name)}
                    className="h-6 px-2 text-[10.5px] font-semibold rounded border border-amber-300 text-amber-700 hover:bg-amber-100 whitespace-nowrap"
                    title="このフォルダーをクローラーに追加"
                    data-testid={`nas-browser-add-${f.name}`}>
                    + 追加
                  </button>
                </div>
              ))}
            </div>
          )}
          {data.files?.length > 0 && (
            <div>
              {data.files.map((f) => (
                <div key={f.path} className="px-3 py-1.5 border-t border-zinc-200 flex items-center gap-2"
                  data-testid={`nas-browser-file-${f.name}`}>
                  <FileIcon size={13} className="text-zinc-400 shrink-0" />
                  <span className="text-[12px] font-mono text-zinc-700 truncate flex-1 min-w-0">{f.name}</span>
                  {f.crawlable === false && f.skip_reason && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 shrink-0"
                      data-testid={`nas-browser-skip-${f.name}`}>対象外: {f.skip_reason}</span>
                  )}
                  <span className="text-[10.5px] text-zinc-400 shrink-0 tabular-nums">{fmtBytes(f.size)}</span>
                  {f.ext && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 shrink-0">{f.ext}</span>}
                </div>
              ))}
            </div>
          )}
          <div className="px-3 py-1.5 border-t border-zinc-200 text-[10.5px] text-zinc-400 flex flex-wrap items-center gap-3">
            <span>フォルダー {data.total_folders} · ファイル {data.total_files} · オブジェクト計 {data.total_objects?.toLocaleString?.() ?? data.total_objects}</span>
            {(data.hidden_folder_count > 0 || data.hidden_file_count > 0) && !showHidden && (
              <span className="text-zinc-500" data-testid="nas-browser-hidden-count">
                非表示: フォルダー {data.hidden_folder_count} / ファイル {data.hidden_file_count}
              </span>
            )}
            {data.unknown_type_count > 0 && (
              <span className="text-amber-600" data-testid="nas-browser-unknown-type">
                対応外の項目 {data.unknown_type_count} 件は表示していません
              </span>
            )}
            {data.truncated && <span className="text-amber-600 font-semibold">結果が多すぎるため一部のみ表示しています（100件まで）</span>}
          </div>
        </>
      )}
    </div>
  );
}


// ── NASクロール認証 — surfaces the safe server-side credential health.
// Never renders token content, length, prefix, suffix, hash or fingerprint.
const AUTH_HEALTH_STYLE = {
  available:              { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  not_configured:         { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   dot: 'bg-amber-500' },
  authentication_failed:  { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-800',    dot: 'bg-rose-500' },
  unreachable:            { bg: 'bg-zinc-50',    border: 'border-zinc-200',    text: 'text-zinc-700',    dot: 'bg-zinc-500' },
};

function NasAuthHealthBadge() {
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/crawler/nas/auth-health`, { headers: authHeader() });
      setH(r.data);
    } catch (e) {
      setH({ ok: false, state: 'unreachable',
             label_ja: 'NASクロール認証: 接続先に到達できません',
             error: e?.response?.data?.detail || null });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!h && !loading) return null;
  const style = AUTH_HEALTH_STYLE[h?.state] || AUTH_HEALTH_STYLE.unreachable;
  return (
    <div className={`rounded-lg border ${style.border} ${style.bg} px-3 py-2 flex flex-wrap items-center gap-2`}
      data-testid="nas-auth-health-badge" data-state={h?.state || 'loading'}>
      <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`} aria-hidden />
      <span className={`text-[12px] font-semibold ${style.text}`}>
        NASクロール認証: {loading ? '確認中…' : (h?.label_ja || '不明')}
      </span>
      {h?.endpoint_host && (
        <span className="text-[11px] text-zinc-500 font-mono" data-testid="nas-auth-health-host">
          {h.endpoint_host}
        </span>
      )}
      {h?.state === 'not_configured' && (
        <span className="text-[11px] text-amber-800">
          {h.url_configured ? '' : 'NAS_CRAWLER_URL '}
          {(!h.url_configured && !h.token_configured) ? 'と ' : ''}
          {h.token_configured ? '' : 'NAS_CRAWLER_TOKEN '}
          が未設定です
        </span>
      )}
      <div className="flex-1" />
      <Button size="sm" variant="outline" onClick={load} disabled={loading}
        className="h-6 text-[11px]" data-testid="nas-auth-health-refresh">
        <ArrowsClockwise size={11} className="mr-1" /> 再確認
      </Button>
    </div>
  );
}


// ── 機密フォルダーガード（人事・給与・個人情報・経理は常に除外） ─────────────
function NasGuardCard() {
  const [g, setG] = useState(null);
  const [word, setWord] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/crawler/nas/guard`, { headers: authHeader() });
      setG(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (words) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/crawler/nas/guard`,
        { extra_keywords: words }, { headers: authHeader() });
      setG({ ...g, extra_keywords: r.data.extra_keywords });
      setWord('');
      if ((r.data.now_blocked_paths || []).length > 0) {
        toast.warning(`登録済みのパスが対象外になりました: ${r.data.now_blocked_paths.join('、')}`);
      } else {
        toast.success('除外ワードを更新しました');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  if (!g) return null;
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/50" data-testid="nas-guard-card">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <p className="text-[12px] font-semibold text-rose-900">機密フォルダーの除外（常に有効）</p>
        <span className="text-[11px] text-rose-800">既定 {g.defaults.length}語 + 追加 {g.extra_keywords.length}語</span>
        <button type="button" onClick={() => setOpen(!open)}
          className="ml-auto text-[11px] font-semibold text-rose-900 underline"
          data-testid="nas-guard-toggle">{open ? '閉じる' : '詳細を見る'}</button>
      </div>
      <p className="px-3 pb-2 text-[11px] text-rose-900">{g.note}</p>
      {open && (
        <div className="border-t border-rose-200 px-3 py-2" data-testid="nas-guard-body">
          <p className="text-[11px] font-semibold text-zinc-700">既定の禁止ワード（削除できません）</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {g.defaults.map((w) => (
              <span key={w} className="rounded-full border border-zinc-300 bg-white px-2 py-0.5 text-[11px] text-zinc-700"
                data-testid={`nas-guard-default-${w}`}>{w}</span>
            ))}
          </div>
          <p className="mt-3 text-[11px] font-semibold text-zinc-700">追加した禁止ワード</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {g.extra_keywords.map((w) => (
              <span key={w} className="flex items-center gap-1 rounded-full border border-rose-300 bg-white px-2 py-0.5 text-[11px] text-rose-900"
                data-testid={`nas-guard-extra-${w}`}>
                {w}
                <button type="button" disabled={busy}
                  onClick={() => save(g.extra_keywords.filter((x) => x !== w))}
                  className="font-bold" data-testid={`nas-guard-remove-${w}`}>×</button>
              </span>
            ))}
            {g.extra_keywords.length === 0 && <span className="text-[11px] text-zinc-500">追加はありません。</span>}
          </div>
          <div className="mt-2 flex gap-2">
            <Input value={word} onChange={(e) => setWord(e.target.value)}
              placeholder="例: 営業秘密" className="h-8 w-48 text-[12px]"
              data-testid="nas-guard-input" />
            <Button size="sm" variant="outline" className="h-8 text-[11px]" disabled={busy || !word.trim()}
              onClick={() => save([...g.extra_keywords, word.trim()])}
              data-testid="nas-guard-add">ワードを追加</Button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Beta-0.5D-A · Retroactive privacy purge (Danger Zone) ─────────────
function PrivacyPurgeDangerZone() {
  const preview = async () => {
    const r = await axios.post(`${API}/api/evidence/privacy/purge-preview`, {},
      { headers: authHeader() });
    return {
      affected: (r.data?.search_index_rows || 0) + (r.data?.kb_embeddings_chunks || 0),
      unit: '件',
      unrecoverable: true,
      detailJa: `検索索引 ${r.data?.search_index_rows || 0} 件 · 意味検索チャンク ${r.data?.kb_embeddings_chunks || 0} 件`,
    };
  };
  const confirmPurge = async () => {
    const r = await axios.post(`${API}/api/evidence/privacy/purge`,
      { confirm: true, reason: 'admin_retroactive_purge_from_ai_learning' },
      { headers: authHeader() });
    return {
      ok: true,
      deleted: (r.data?.deleted_search_index_rows || 0) + (r.data?.deleted_kb_embeddings_chunks || 0),
      message_ja: r.data?.message_ja,
    };
  };
  return (
    <DangerZone titleJa="機密除外の遡及適用"
      descriptionJa="除外ルールを追加した後、すでに索引済みのデータを削除します。"
      testid="danger-privacy-purge">
      <DangerAction
        testid="danger-privacy-purge-action"
        titleJa="検索索引から除外対象を削除"
        consequenceJa="この操作は取り消せません。ただし NAS 上のファイル・バックアップ・DMS 本体は絶対に削除しません。派生する検索索引の行だけを削除します。"
        previewButton={{ labelJa: '影響を確認', onPreview: preview }}
        confirmPromptJa="削除する"
        onConfirm={confirmPurge}
      />
    </DangerZone>
  );
}


// ── スコープ監査（承認範囲の単一情報源） ────────────────────────────────
function ScopeAuditCard({ refreshKey }) {
  const [audit, setAudit] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/crawler/nas/scope-audit`, { headers: authHeader() });
      setAudit(r.data);
    } catch (e) { setAudit(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = async () => {
    const r = await axios.get(`${API}/api/crawler/nas/scope-cleanup/preview`, { headers: authHeader() });
    return {
      affected: r.data?.total || 0,
      unit: '件',
      unrecoverable: true,
      detailJa: `検索索引 ${r.data?.search_index_rows || 0} 件 · ベクトル ${r.data?.embeddings || 0} 件 · AIチャンク ${r.data?.ai_chunks || 0} 件 · カタログ ${r.data?.catalogue_entries || 0} 件`,
    };
  };
  const confirmCleanup = async () => {
    const r = await axios.post(`${API}/api/crawler/nas/scope-cleanup`, { confirm: true }, { headers: authHeader() });
    const d = r.data?.deleted || {};
    await load();
    return { ok: true, deleted: (d.search_index_rows || 0) + (d.ai_chunks || 0) + (d.catalogue_entries || 0) };
  };

  const inc = audit?.inconsistencies || {};
  const drift = (inc.index_out_of_scope || 0) + (inc.catalogue_out_of_scope || 0);

  return (
    <div className="rounded-lg border border-zinc-200" data-testid="scope-audit-card">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 flex items-center gap-2 text-left" data-testid="scope-audit-toggle">
        <ShieldCheck size={14} className="text-teal-700 shrink-0" />
        <span className="text-[12px] font-semibold text-zinc-700">スコープ監査（承認範囲の単一情報源）</span>
        {drift > 0 && (
          <span className="text-[10.5px] px-1.5 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 font-semibold"
            data-testid="scope-audit-drift-badge">不整合 {drift} 件</span>
        )}
        <span className="ml-auto text-[11px] text-zinc-400">{open ? '閉じる' : '開く'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-zinc-200 pt-2.5">
          <p className="text-[11px] text-zinc-600">
            {audit?.message_ja || '「クロール対象パス」が承認範囲の唯一の情報源です。'}
          </p>
          {loading && <p className="text-[11px] text-zinc-400">読み込み中…</p>}
          {audit && !audit.scope_configured && (
            <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-900"
              data-testid="scope-audit-unconfigured">
              承認範囲が未設定です。この状態では検索・AI回答・NASアーカイブのいずれからもNASの内容は参照できません。
            </p>
          )}
          {audit && (
            <>
              <div className="rounded border border-zinc-200 overflow-hidden" data-testid="scope-audit-roots">
                <div className="px-2 py-1 bg-zinc-50 border-b border-zinc-200 text-[11px] font-semibold text-zinc-600">承認フォルダー</div>
                {(audit.approved_roots || []).length === 0 ? (
                  <p className="px-2 py-2 text-[11px] text-zinc-400">なし</p>
                ) : audit.approved_roots.map((r) => (
                  <div key={r.prefix} className="px-2 py-1.5 border-t border-zinc-100 first:border-t-0 flex flex-wrap items-center gap-2 text-[11px]"
                    data-testid={`scope-audit-root-${r.prefix}`}>
                    <span className="font-mono text-zinc-800">{r.prefix}</span>
                    <span className={`px-1.5 py-0.5 rounded-full border font-semibold ${r.visible
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
                      {r.visible ? '表示可' : '除外中'}
                    </span>
                    <span className="text-zinc-500">閲覧: {(r.acl_roles || []).join('・')}</span>
                    <span className="text-zinc-500 tabular-nums">索引 {r.indexed_files} 件</span>
                    <span className="text-zinc-500 tabular-nums">フォルダー {r.catalogue_folders} 件</span>
                    {!r.visible && r.reason && <span className="text-rose-700">{r.reason}</span>}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="scope-audit-counters">
                {[['表示可能フォルダー', audit.visible_paths?.count],
                  ['索引済み（範囲内）', audit.indexed_paths?.total_in_scope],
                  ['AI参照可能', audit.ai_retrievable_paths?.count],
                  ['範囲外の索引', inc.index_out_of_scope]].map(([l, v], i) => (
                  <div key={l} className={`rounded border px-2 py-1.5 ${i === 3 && (v || 0) > 0
                    ? 'border-rose-200 bg-rose-50' : 'border-zinc-200 bg-white'}`}>
                    <p className="text-[10.5px] text-zinc-500">{l}</p>
                    <p className={`text-[14px] font-bold tabular-nums ${i === 3 && (v || 0) > 0 ? 'text-rose-700' : 'text-zinc-900'}`}>
                      {v ?? 0}
                    </p>
                  </div>
                ))}
              </div>

              <div className="rounded border border-zinc-200 px-2 py-1.5" data-testid="scope-audit-excluded">
                <p className="text-[11px] font-semibold text-zinc-600 mb-1">除外ルール（承認範囲内でも参照しません）</p>
                {[['管理者が除外したパス', audit.excluded?.excluded_paths],
                  ['保護ルート', audit.excluded?.protected_roots],
                  ['管理者の追加禁止ワード', audit.excluded?.admin_keywords],
                  ['個人フォルダー（管理者）', audit.excluded?.admin_personal_patterns]].map(([l, arr]) => (
                  <p key={l} className="text-[11px] text-zinc-600">
                    {l}: {(arr || []).length ? <span className="font-mono">{arr.join(' / ')}</span> : <span className="text-zinc-400">なし</span>}
                  </p>
                ))}
                <p className="text-[11px] text-zinc-500 mt-1">
                  既定の機密ワード {(audit.excluded?.system_keywords || []).length} 件 ·
                  個人フォルダー既定 {(audit.excluded?.system_personal_patterns || []).length} 件（削除できません）
                </p>
              </div>

              <DangerZone titleJa="承認範囲の反映（範囲外データの削除）"
                descriptionJa="承認範囲から外れたフォルダーの派生データ（検索索引・ベクトル・AIチャンク・カタログ）を削除します。"
                testid="danger-scope-cleanup">
                <DangerAction
                  testid="danger-scope-cleanup-action"
                  titleJa="範囲外データを削除して整合させる"
                  consequenceJa="この操作は取り消せません。NAS上のファイル・バックアップ・DMS本体は削除しません。派生データのみを削除します。"
                  previewButton={{ labelJa: '影響を確認', onPreview: preview }}
                  confirmPromptJa="削除する"
                  onConfirm={confirmCleanup}
                />
              </DangerZone>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ── NAS crawler card ──────────────────────────────────────────────────
function NasCrawler({ onStatsChanged }) {
  const [cfg, setCfg] = useState(null);
  const [supported, setSupported] = useState([]);
  const [ping, setPing] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [requestedMode, setRequestedMode] = useState(null);
  const [previewModes, setPreviewModes] = useState(null);   // 種別ごとの可否（エージェント能力）
  const [previewJob, setPreviewJob] = useState(null);       // 現在／直近のプレビュー
  const [previewHistory, setPreviewHistory] = useState([]);
  const [previewPollError, setPreviewPollError] = useState(null);
  const [detail, setDetail] = useState(null);               // { kind, id, context }
  const [job, setJob] = useState(null);
  const [crawlerCfg, setCrawlerCfg] = useState({});
  const [caps, setCaps] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scopeKey, setScopeKey] = useState(0);
  const [ingest, setIngest] = useState(null);        // /nas/ingest/active の応答
  const [ingestBusy, setIngestBusy] = useState(false);
  const [ingestAction, setIngestAction] = useState(null);
  const [ingestDetailId, setIngestDetailId] = useState(null);
  const pollRef = useRef(null);
  const previewPollRef = useRef(null);
  const ingestPollRef = useRef(null);
  const detailTriggerRef = useRef(null);
  const ingestTriggerRef = useRef(null);

  const openDetail = (target, e) => {
    detailTriggerRef.current = e?.currentTarget || null;
    setDetail(target);
  };

  const load = async () => {
    try {
      const [c, j] = await Promise.all([
        axios.get(`${API}/api/crawler/nas/config`, { headers: authHeader() }),
        axios.get(`${API}/api/crawler/jobs`, { params: { kind: 'nas', limit: 10 }, headers: authHeader() }),
      ]);
      setCfg({ paths: c.data.paths || [], extensions: c.data.extensions || [], max_bytes: c.data.max_bytes });
      setSupported(c.data.supported_extensions || []);
      // クローラー経路の状態（ストレージ/WANの健全性とは別物）
      setPing(c.data.crawler_status || {});
      setCrawlerCfg(c.data.crawler_config || {});
      setHistory(j.data || []);
      const active = (j.data || []).find((x) => ['running', 'paused'].includes(x.status)) || (j.data || [])[0] || null;
      setJob(active);
      setDirty(false);
    } catch (e) { /* silent */ }
    try {
      const k = await axios.get(`${API}/api/crawler/nas/capabilities`, { headers: authHeader() });
      setCaps(k.data || null);
    } catch (e) { setCaps(null); }
    // 実行中のプレビューへ再接続する（ページ再読込でも新規ジョブは作らない）
    try {
      const [a, h] = await Promise.all([
        axios.get(`${API}/api/crawler/nas/preview/active`, { headers: authHeader() }),
        axios.get(`${API}/api/crawler/nas/preview/history`, { params: { limit: 5 }, headers: authHeader() }),
      ]);
      setPreviewJob(a.data?.active || a.data?.latest || null);
      setPreviewModes(a.data?.preview_modes || null);
      setPreviewHistory((h.data?.jobs || []).slice(1));
      setPreviewPollError(null);
    } catch (e) { /* silent — プレビュー未実行でも設定画面は使えるべき */ }
    // カーソル取り込み（v1.4.0）。フラグ OFF でも状態表示は取得できる。
    try {
      const r = await axios.get(`${API}/api/crawler/nas/ingest/active`,
        { headers: authHeader() });
      setIngest(r.data || null);
    } catch (e) { /* silent — 取り込み未実行でも設定画面は使えるべき */ }
  };  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (job?.status === 'running') {
      pollRef.current = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/api/crawler/${job.id}`, { headers: authHeader() });
          setJob(r.data);
          if (r.data.status !== 'running') { onStatsChanged?.(); clearInterval(pollRef.current); }
        } catch { /* */ }
      }, 2500);
      return () => clearInterval(pollRef.current);
    }
  }, [job?.id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // プレビュージョブのポーリング。終端で必ず停止し、失敗しても新規ジョブは作らない。
  const previewActive = !!previewJob && ['queued', 'running'].includes(previewJob.status);
  const refreshPreview = async (jobId) => {
    const id = jobId || previewJob?.job_id;
    if (!id) return;
    try {
      const r = await axios.get(`${API}/api/crawler/nas/preview/${id}`, { headers: authHeader() });
      setPreviewJob(r.data);
      setPreviewPollError(null);
      if (r.data?.is_terminal) load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setPreviewPollError((d && typeof d === 'object' ? d.message : d) || '状態の取得に失敗しました');
    }
  };

  useEffect(() => {
    if (!previewActive) return undefined;
    const id = previewJob?.job_id;
    const ms = (previewJob?.poll_interval_seconds || 3) * 1000;
    previewPollRef.current = setInterval(() => { refreshPreview(id); }, ms);
    return () => clearInterval(previewPollRef.current);
  }, [previewActive, previewJob?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── カーソル取り込み（v1.4.0）─────────────────────────────────────────
  // 画面の操作は助言的。実際の可否はサーバーが必ず再判定する。
  // `/nas/ingest/active` は実行中を `active`、直近を `latest` で返す。
  const ingestJob = ingest?.active || ingest?.latest || null;
  const ingestActive = !!ingestJob?.is_active;

  const refreshIngest = async () => {
    try {
      const r = await axios.get(`${API}/api/crawler/nas/ingest/active`,
        { headers: authHeader() });
      setIngest(r.data || null);
      const j = r.data?.active || r.data?.latest;
      if (j && !j.is_active) onStatsChanged?.();
    } catch (e) { /* silent — 表示だけの取得で画面を壊さない */ }
  };

  useEffect(() => {
    if (!ingestActive) return undefined;
    ingestPollRef.current = setInterval(refreshIngest, 3000);
    return () => clearInterval(ingestPollRef.current);
  }, [ingestActive, ingestJob?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ingestErr = (e, fallback) => {
    const d = e?.response?.data?.detail;
    toast.error((d && typeof d === 'object' ? d.message : d) || fallback);
    refreshIngest();
  };

  const runIngestAction = async (name, fn, okMsg) => {
    if (ingestBusy) return;
    setIngestBusy(true); setIngestAction(name);
    try {
      const r = await fn();
      if (r?.data?.job) {
        setIngest((p) => ({ ...(p || {}), active: r.data.job, latest: r.data.job }));
      }
      if (okMsg) toast.success(okMsg);
      await refreshIngest();
    } catch (e) {
      ingestErr(e, '操作に失敗しました');
    } finally { setIngestBusy(false); setIngestAction(null); }
  };

  const startIngest = () => runIngestAction('start',
    () => axios.post(`${API}/api/crawler/nas/ingest/start`, {},
      { headers: authHeader() }),
    'カーソル取り込みを開始しました');

  const pauseIngest = () => runIngestAction('pause',
    () => axios.post(`${API}/api/crawler/nas/ingest/${ingestJob.id}/pause`, {},
      { headers: authHeader() }),
    '一時停止を要求しました（安全な区切りで停止します）');

  const resumeIngest = () => runIngestAction('resume',
    () => axios.post(`${API}/api/crawler/nas/ingest/${ingestJob.id}/resume`, {},
      { headers: authHeader() }),
    '保存済みの位置から再開しました');

  const cancelIngest = () => runIngestAction('cancel',
    () => axios.post(`${API}/api/crawler/nas/ingest/${ingestJob.id}/cancel`, {},
      { headers: authHeader() }),
    '中止を要求しました（途中までの結果と位置は保存されます）');

  const ingestStartBlocked = (() => {
    if (!ingest?.cursor_ingestion_enabled) {
      return '「NASカーソル取り込み」フラグが OFF のため開始できません。';
    }
    if (ingestActive) return 'すでに取り込みが実行中です。';
    if (ingestJob?.can_resume) {
      return '一時停止中の取り込みがあります。まず「再開」してください。';
    }
    if (job && ['running', 'queued'].includes(job.status)) {
      return '従来方式のクロールが実行中です。完了を待ってください。';
    }
    return null;
  })();

  const addPath = () => setCfg((c) => ({ ...c, paths: [...(c?.paths || []), { prefix: '', label: '', acl_roles: ['admin'] }] })) || setDirty(true);  const updatePath = (i, k, v) => { setCfg((c) => { const paths = [...c.paths]; paths[i] = { ...paths[i], [k]: v }; return { ...c, paths }; }); setDirty(true); };
  const removePath = (i) => { setCfg((c) => ({ ...c, paths: c.paths.filter((_, x) => x !== i) })); setDirty(true); };
  const toggleExt = (e) => { setCfg((c) => { const s = new Set(c.extensions); s.has(e) ? s.delete(e) : s.add(e); return { ...c, extensions: [...s] }; }); setDirty(true); };
  const setMax = (mb) => { setCfg((c) => ({ ...c, max_bytes: mb * 1024 * 1024 })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { paths: cfg.paths.filter((p) => p.prefix.trim()), extensions: cfg.extensions, max_bytes: cfg.max_bytes };
      const r = await axios.put(`${API}/api/crawler/nas/config`, payload, { headers: authHeader() });
      const cl = r.data?.scope_cleanup || {};
      toast.success('NAS クローラー設定を保存しました');
      if (cl.auto_applied) {
        const d = cl.deleted || {};
        toast.info(`承認範囲外になった派生データを削除しました（索引 ${d.search_index_rows || 0} 件 · カタログ ${d.catalogue_entries || 0} 件）`);
      } else if (cl.confirmation_required) {
        toast.warning(cl.message_ja || '承認範囲外の派生データが多いため、スコープ監査から反映してください', { duration: 10000 });
      }
      setScopeKey((k) => k + 1);
      setDirty(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const preview = async (mode) => {
    if (previewing || previewActive) return;   // 二重送信をしない
    setPreviewing(true);
    setRequestedMode(mode);
    setPreviewPollError(null);
    try {
      const r = await axios.post(`${API}/api/crawler/nas/preview/start`,
        { preview_mode: mode }, { headers: authHeader() });
      setPreviewJob(r.data?.job || null);
      if (r.data?.preview_modes) setPreviewModes(r.data.preview_modes);
      if (r.data?.reused_existing) toast.info('すでに実行中のプレビューに接続しました');
      else if (mode === 'full_validation') {
        toast.success('完全検証プレビューを開始しました（全ルートをカーソルで走査します）');
      } else {
        toast.success('簡易見積りを開始しました（取り込みは許可されません）');
      }
      refreshPreview(r.data?.job_id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === 'object' && d.preview_modes) setPreviewModes(d.preview_modes);
      toast.error((d && typeof d === 'object' ? d.message : d) || 'プレビューの開始に失敗しました');
    }
    finally { setPreviewing(false); setRequestedMode(null); }
  };

  const cancelPreview = async () => {
    const id = previewJob?.job_id;
    if (!id) return;
    try {
      const r = await axios.post(`${API}/api/crawler/nas/preview/${id}/cancel`, {}, { headers: authHeader() });
      setPreviewJob(r.data?.job || previewJob);
      toast.info(r.data?.message_ja || '中止を要求しました');
      refreshPreview(id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === 'object' ? d.message : d) || '中止要求に失敗しました');
    }
  };

  const start = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/crawler/nas/start`, {}, { headers: authHeader() });
      const j = await axios.get(`${API}/api/crawler/${r.data.id}`, { headers: authHeader() });
      setJob(j.data);
      toast.success('NAS クロールを開始しました');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === 'object' ? d.message : d) || 'クロールの開始に失敗しました');
    }
    finally { setBusy(false); }
  };
  const doAction = async (path) => {
    if (!job) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/crawler/${job.id}/${path}`, {}, { headers: authHeader() });
      const j = await axios.get(`${API}/api/crawler/${job.id}`, { headers: authHeader() });
      setJob(j.data); load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${path} に失敗しました`); }
    finally { setBusy(false); }
  };

  // バックエンドが唯一の権威。ここは助言的な活性制御のみ（サーバー側で必ず再検証する）。
  const gate = caps?.ingestion_gate || null;
  const canStart = ping?.ok && caps?.crawler_mode === 'native' && gate?.allowed === true
    && !previewActive
    && (cfg?.paths || []).some((p) => p.prefix?.trim())
    && (!job || ['complete', 'cancelled', 'failed'].includes(job.status));
  const canPause = job?.status === 'running';
  const canResume = job?.status === 'paused' && ping?.ok && caps?.ingestion_allowed !== false;
  const canCancel = ['running', 'paused'].includes(job?.status);
  const maxMB = Math.round((cfg?.max_bytes || 0) / 1024 / 1024);
  const previewState = derivePreviewState(previewJob);
  const jobState = deriveCrawlState(job);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="ai-learning-nas-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-700 flex items-center justify-center shrink-0">
          <HardDrives size={18} weight="duotone" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-zinc-900">NAS ドキュメント</h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">Asustor NAS 上の PDF / Office / テキストを AI が学習できるようにインデックス化</p>
        </div>
        <div className={`text-[11px] px-2 py-1 rounded-full font-semibold shrink-0 ${
          ping?.ok
            ? (caps?.crawler_mode && caps.crawler_mode !== 'native'
                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                : 'bg-emerald-50 text-emerald-700 border border-emerald-200')
            : 'bg-rose-50 text-rose-700 border border-rose-200'}`}
          data-testid="nas-ping-badge" data-category={ping?.category || ''}
          data-crawler-mode={caps?.crawler_mode || ''}>
          {ping?.ok
            ? (caps?.crawler_mode === 'browse_only'
                ? `接続済み・フォルダー閲覧のみ (${ping.latency_ms}ms)`
                : caps?.crawler_mode === 'legacy_tree_objects'
                ? `一時互換モード (${ping.latency_ms}ms)`
                : `${TRANSPORT_LABEL[ping.transport] || ping.transport} OK (${ping.latency_ms}ms)`)
            : (ping?.category === 'not_configured' ? 'クローラー未設定' : 'クローラー未接続')}
        </div>
      </div>
      {!ping?.ok && (
        <div className="px-5 py-3 bg-rose-50 border-b border-rose-200 text-[12px] text-rose-900" data-testid="nas-offline-notice">
          <Warning size={14} weight="fill" className="inline mr-1 text-rose-600" />
          <b>クローラーが NAS エージェントに接続できません。</b> {ping?.message}
          <span className="block mt-0.5 text-[11px] text-rose-800">
            接続先: {crawlerCfg?.endpoint_host
              ? `${crawlerCfg.endpoint_scheme}://${crawlerCfg.endpoint_host}${crawlerCfg.endpoint_port ? `:${crawlerCfg.endpoint_port}` : ''}`
              : '未設定'}
            （{crawlerCfg?.crawler_url_configured ? 'NAS_CRAWLER_URL'
              : crawlerCfg?.using_legacy_storage_url ? '互換モード: NAS_STORAGE_URL'
              : 'NAS_CRAWLER_URL が未設定'}）
            {' · '}分類: {ping?.category}
            {' · '}フォールバック: {ping?.fallback_enabled ? '有効' : '無効'}
            {ping?.fallback_configured && !ping?.fallback_enabled && '（URLは設定済みですが未使用）'}
          </span>
        </div>
      )}
      {ping?.ok && (
        <div className="px-5 py-2 bg-zinc-50 border-b border-zinc-200 text-[11px] text-zinc-600 flex flex-wrap gap-x-4 gap-y-1"
          data-testid="nas-endpoint-info">
          <span>経路: <b className="text-zinc-800">{TRANSPORT_LABEL[ping.transport] || ping.transport}</b></span>
          {ping.transport === 'legacy-storage-endpoint' && (
            <span className="text-amber-700 font-semibold">
              NAS_CRAWLER_URL が未設定のため互換モードで動作中です（本番では設定してください）
            </span>
          )}
          {crawlerCfg?.insecure_transport && (
            <span className="text-rose-700 font-semibold" data-testid="nas-insecure-transport">
              暗号化されていない HTTP 経路です。社外ネットワークを経由する場合は HTTPS にしてください
            </span>
          )}
          <span>接続先: <span className="font-mono">{ping.endpoint}</span></span>
          <span>エージェント: v{ping.agent_version || '—'}</span>
          <span>クロールルート: <b className="text-zinc-800">{ping.crawl_root ? '設定済み' : '未取得'}</b></span>
          {ping.capabilities && ping.capabilities.crawler_api === false && (
            <span className="text-amber-700 font-semibold">
              フォルダー一覧APIなし → 旧方式（オブジェクト一覧）で動作中。エージェントを v1.3.0 に更新してください。
            </span>
          )}
          {ping.crawl_root_mismatch && (
            <span className="text-amber-700 font-semibold" data-testid="nas-root-mismatch">
              設定値 SENSBEY_NAS_CRAWL_ROOT と実際のクロールルートが異なります
            </span>
          )}
        </div>
      )}

      <div className="px-5 py-4 space-y-4">
        {caps && caps.crawler_mode && caps.crawler_mode !== 'native' && (
          <div className={`rounded-lg border px-3 py-2.5 text-[12px] ${
            caps.crawler_mode === 'legacy_tree_objects'
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-rose-50 border-rose-200 text-rose-900'}`}
            data-testid="nas-mode-banner" data-mode={caps.crawler_mode}>
            <div className="flex items-start gap-2">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div className="min-w-0">
                {caps.crawler_mode === 'legacy_tree_objects' && (
                  <span className="inline-block mb-1 text-[10.5px] px-2 py-0.5 rounded-full font-bold bg-amber-100 border border-amber-300 text-amber-900"
                    data-testid="nas-mode-legacy-pill">
                    一時互換モード / Temporary compatibility mode
                  </span>
                )}
                <p className="font-semibold" data-testid="nas-mode-message-ja">{caps.message_ja}</p>
                <p className="mt-0.5 text-[11px] opacity-80" data-testid="nas-mode-message-en">{caps.message_en}</p>
                <p className="mt-1 text-[11px] opacity-90" data-testid="nas-mode-caps">
                  フォルダー閲覧: {caps.capabilities?.tree_api ? '可' : '不可'}
                  {' · '}ファイル列挙API: {caps.capabilities?.files_api ? '可' : 'なし'}
                  {' · '}ファイル取得API: {caps.capabilities?.file_api ? '可' : 'なし'}
                  {caps.crawler_mode === 'browse_only' && ' · 事前探索と取り込みは停止中'}
                </p>
                <p className="mt-1 text-[11px] opacity-90" data-testid="nas-mode-preflight">
                  互換性実証: <b>{PREFLIGHT_STATUS_LABEL[caps.preflight_status] || caps.preflight_status || '—'}</b>
                  {caps.preflight_reason && <>（理由: <span className="font-mono">{caps.preflight_reason}</span>）</>}
                  {caps.preflight_reason_ja && <> — {caps.preflight_reason_ja}</>}
                </p>
              </div>
            </div>
          </div>
        )}
        {caps && caps.crawler_mode === 'native' && (
          <div className="hidden" data-testid="nas-mode-banner" data-mode="native" />
        )}
        {/* A. 現在のプレビュー — 過去の実行結果とは別枠。再読込でも再接続する。 */}
        <div className="space-y-2" data-testid="nas-preview-section">
          <div className="flex items-center gap-2">
            <p className="text-[11px] font-semibold text-zinc-500">
              クロール前プレビュー / Pre-crawl preview
            </p>
          </div>
          <NasPreviewModePanel modes={previewModes} connected={!!ping?.ok
            && caps?.preview_allowed !== false}
            busy={previewing || previewActive} activeMode={requestedMode}
            lastKnownVersion={previewJob?.agent_version}
            onStart={preview} />
          {previewJob ? (
            <NasPreviewProgressCard job={previewJob} pollError={previewPollError}
              onCancel={cancelPreview} onRefresh={() => refreshPreview()}
              onOpenDetails={(e) => openDetail({
                kind: 'preview', id: previewJob.job_id,
                context: previewJob.is_active ? 'current_preview' : 'latest_preview' }, e)} />
          ) : (
            <p className="text-[12px] text-zinc-400 rounded-lg border border-dashed border-zinc-200 px-3 py-2.5"
              data-testid="nas-preview-empty">
              プレビューは未実行です。「クロール前プレビュー」で対象ファイルを確認してください。
              <span className="block text-[11px]">No preview has been run yet.</span>
            </p>
          )}
          {previewJob && (
            <div className="hidden" data-testid="nas-preview-result">
              <span data-testid="nas-preview-mode" data-mode={previewJob.crawler_mode}>
                実行モード: {previewJob.crawler_mode === 'native' ? '標準NASクローラー'
                  : previewJob.crawler_mode === 'legacy_tree_objects' ? '一時互換アダプター'
                    : 'フォルダー閲覧のみ'}
              </span>
            </div>
          )}
        </div>

        {/* B. カーソル取り込み（v1.4.0）— 大規模・有界・再開可能。既定は無効。 */}
        <div className="space-y-1.5" data-testid="nas-ingest-section">
          <p className="text-[11px] font-semibold text-zinc-500">
            カーソル取り込み / Cursor ingestion
            <span className="ml-1 font-normal text-zinc-400">
              （大規模向け・上限あり・中断して再開できます）
            </span>
          </p>
          <NasIngestProgressPanel
            data={ingest} job={ingestJob}
            busy={ingestBusy} action={ingestAction}
            canStart={!ingestStartBlocked}
            startBlockedReason={ingestStartBlocked}
            onStart={startIngest} onPause={pauseIngest}
            onResume={resumeIngest} onCancel={cancelIngest}
            onRefresh={refreshIngest}
            onOpenDetails={(e) => {
              ingestTriggerRef.current = e?.currentTarget || null;
              setIngestDetailId(ingestJob?.id || null);
            }} />
        </div>

        {/* C. 過去の実行結果 — 現在のプレビューの結果ではない。 */}
        {job && (
          <div className="space-y-1.5" data-testid="nas-history-section">
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-semibold text-zinc-500" data-testid="nas-job-historical-label">
                {['running', 'paused'].includes(job.status)
                  ? '実行中の NAS クロール / Active NAS crawl'
                  : '過去の実行結果 / Historical run result'}
              </p>
              <div className="flex-1" />
              <Button onClick={(e) => openDetail({ kind: 'crawl', id: job.id,
                context: ['running', 'paused'].includes(job.status) ? 'current_preview' : 'historical_crawl' }, e)}
                variant="outline" className="h-8 text-[12px]" data-testid="nas-events-btn">
                <ListMagnifyingGlass size={13} className="mr-1" /> 明細を見る / View details
              </Button>
            </div>
            <JobStatusCard job={job} testId="nas-job" state={jobState} />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button onClick={start} disabled={busy || !canStart}
            className="h-9 bg-amber-600 hover:bg-amber-700 text-[13px]" data-testid="nas-start-btn">
            <Play size={13} weight="fill" className="mr-1" /> NAS クロール開始
          </Button>
          {canPause && <Button onClick={() => doAction('pause')} disabled={busy} variant="outline" className="h-9 text-[13px]" data-testid="nas-pause-btn"><Pause size={13} weight="fill" className="mr-1" />一時停止</Button>}
          {canResume && <Button onClick={() => doAction('resume')} disabled={busy} className="h-9 bg-teal-700 hover:bg-teal-800 text-[13px]" data-testid="nas-resume-btn"><Play size={13} weight="fill" className="mr-1" />再開</Button>}
          {canCancel && <Button onClick={() => doAction('cancel')} disabled={busy} variant="outline" className="h-9 text-[13px] border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="nas-cancel-btn"><StopCircle size={13} className="mr-1" />中止</Button>}
        </div>
        {gate && gate.allowed !== true && (
          <p className="text-[11.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2"
            data-testid="nas-ingestion-gate-notice" data-gate-reason={gate.reason}>
            <Warning size={13} weight="fill" className="inline mr-1" />
            NAS クロール開始は保留中です: {gate.reason_ja}
            <span className="block text-[11px] opacity-80">{gate.reason_en}</span>
          </p>
        )}

        {/* Confidential folder guard */}
        <NasGuardCard />

        {/* Beta-0.5D-A · Danger Zone (retrofit): retroactive privacy purge. */}
        <PrivacyPurgeDangerZone />

        {/* Configured paths */}
        <div className="rounded-lg border border-zinc-200">
          <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
            <p className="text-[12px] font-semibold text-zinc-700">クロール対象パス</p>
            <Button size="sm" variant="outline" onClick={addPath} className="h-7 text-[11px]" data-testid="nas-add-path"><Plus size={12} className="mr-1" />パスを追加</Button>
          </div>
          {(cfg?.paths || []).some((p) => (p.acl_roles || []).includes('*')) && (
            <p className="mx-3 mb-2 rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11px] font-medium text-rose-800"
              data-testid="nas-acl-wildcard-warning">
              「全員」に設定されたフォルダーがあります。そのフォルダーの本文は全社員のAI回答に引用されます。
              人事・給与・個人情報を含むフォルダーには使用しないでください。
            </p>
          )}
          {(() => {
            const pres = (cfg?.paths || []).map((p) => (p.prefix || '').replace(/\/+$/, '')).filter(Boolean);
            const overlap = pres.some((a, i) => pres.some((b, j) => i !== j && a.startsWith(b + '/')));
            const broad = pres.some((a) => a.split('/').filter(Boolean).length <= 1);
            if (!overlap && !broad) return null;
            return (
              <p className="mx-3 mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-900"
                data-testid="nas-path-scope-warning">
                {broad && '最上位に近いパスが含まれています（NAS全体を取り込む恐れがあります）。'}
                {overlap && '重複するパスがあります（同じファイルを二重に取り込みます）。'}
              </p>
            );
          })()}
          <p className="mx-3 mb-2 rounded border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-[11px] text-zinc-600"
            data-testid="nas-ocr-limitation">
            スキャンしたPDFや画像は文字を読み取れないため「スキップ」になります（NASの文字読み取りは未対応）。
            長い文書は先頭20万文字までを対象とします。
          </p>
          {(!cfg?.paths || cfg.paths.length === 0) ? (
            <p className="px-3 py-4 text-[12px] text-zinc-400 text-center">パスが未設定です。「パスを追加」から NAS 上のフォルダーを指定してください。</p>
          ) : cfg.paths.map((p, i) => (
            <div key={i} className="px-3 py-2 border-t border-zinc-200 first:border-t-0 flex flex-wrap items-center gap-2" data-testid={`nas-path-row-${i}`}>
              <Input value={p.prefix} onChange={(e) => updatePath(i, 'prefix', e.target.value)}
                placeholder="/share/技術/日報アーカイブ" className="h-8 text-[12px] font-mono flex-1 min-w-[220px]"
                data-testid={`nas-path-prefix-${i}`} />
              <Input value={p.label || ''} onChange={(e) => updatePath(i, 'label', e.target.value)}
                placeholder="表示名（任意）" className="h-8 text-[12px] w-40"
                data-testid={`nas-path-label-${i}`} />
              <select
                value={JSON.stringify(p.acl_roles || ['admin'])}
                onChange={(e) => updatePath(i, 'acl_roles', JSON.parse(e.target.value))}
                className="h-8 text-[12px] border border-zinc-200 rounded px-2 bg-white"
                data-testid={`nas-path-acl-${i}`}
              >
                <option value='["admin"]'>管理者のみ</option>
                <option value='["manager","admin"]'>管理職以上</option>
                <option value='["engineer","manager","admin"]'>技術メンバー</option>
                <option value='["*"]'>全員</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => removePath(i)}
                className="h-8 w-8 p-0 border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`nas-path-remove-${i}`}>
                <X size={13} />
              </Button>
            </div>
          ))}
        </div>

        {/* Beta-0.5A P0-B: NAS crawl authentication health (safe — no secrets shown). */}
        <NasAuthHealthBadge />

        {/* 承認範囲の監査（StorageScopeService の単一情報源を可視化） */}
        <ScopeAuditCard refreshKey={scopeKey} />

        {/* NAS ブラウザ — walk the NAS live and pick folders to add to the crawl.
            接続できない場合もカード自体は表示し、日本語の理由を出す（承認範囲を
            設定できなくなるのを防ぐ）。 */}
        <NasBrowser
          onAddPath={(prefix, label) => {
              const rel = prefix.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
              if (!rel) { toast.error('フォルダーを選んでください'); return; }
              const existing = (cfg?.paths || []).map((p) => (p.prefix || '').replace(/^\/+/, '').replace(/\/+$/, ''));
              if (existing.includes(rel)) { toast.info('このパスは既にリストにあります'); return; }
              const clash = existing.find((e) => e && (rel === e || rel.startsWith(e + '/') || e.startsWith(rel + '/')));
              if (clash) { toast.error(`「${clash}」と重複します（親子関係のパスは二重インデックスになります）`); return; }
              setCfg((c) => ({ ...c, paths: [...(c?.paths || []), { prefix: rel, label: label || '', acl_roles: ['admin'] }] }));
              setDirty(true);
              toast.success(`「${rel}」を追加しました。保存を押して確定してください。`);
            }}
        />

        {/* Extensions + max size */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-zinc-200 px-3 py-2.5">
            <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">対象ファイル形式</p>
            <div className="flex flex-wrap gap-1.5" data-testid="nas-ext-toggles">
              {supported.map((e) => {
                const on = cfg?.extensions?.includes(e);
                return (
                  <button key={e} onClick={() => toggleExt(e)} type="button"
                    className={`h-7 px-2.5 text-[11px] rounded-full border font-mono transition-colors ${
                      on ? 'bg-amber-500 border-amber-500 text-white'
                         : 'bg-white border-zinc-300 text-zinc-500 hover:border-amber-300'}`}
                    data-testid={`nas-ext-${e.replace('.','')}`}>
                    {e}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-lg border border-zinc-200 px-3 py-2.5">
            <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">1 ファイルあたり最大サイズ</p>
            <div className="flex items-center gap-2">
              <input type="range" min="1" max="200" value={maxMB} onChange={(e) => setMax(Number(e.target.value))}
                className="flex-1" data-testid="nas-maxbytes-slider" />
              <span className="text-[13px] font-semibold text-zinc-800 tabular-nums w-16 text-right">{maxMB} MB</span>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">超過するファイルは自動でスキップされます。</p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button onClick={save} disabled={saving || !dirty}
            className="h-9 bg-teal-700 hover:bg-teal-800 text-[13px]" data-testid="nas-save-btn">
            <FloppyDisk size={13} className="mr-1" /> {saving ? '保存中…' : '設定を保存'}
          </Button>
        </div>

      </div>

      {(history.length > 1 || previewHistory.length > 0) && (
        <div className="px-5 py-3 border-t border-zinc-200 space-y-3">
          <div>
            <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">
              過去の実行結果 <span className="text-[10.5px] font-normal text-zinc-400">Historical runs</span>
              <span className="block text-[10.5px] font-normal text-zinc-400">
                これは現在のプレビューの結果ではありません / These are not the current preview result.
              </span>
            </p>
            <div className="divide-y divide-zinc-200 border border-zinc-200 rounded text-[11px]" data-testid="nas-history">
              {history.slice(0, 5).map((h, i) => {
                const s = deriveCrawlState(h);
                return (
                  <div key={h.id} className="px-3 py-1.5 flex items-center gap-2"
                    data-testid={`nas-history-row-${i}`} data-state={s.key}>
                    <s.Icon size={12} className={s.color} />
                    <span className={`font-semibold ${s.color} shrink-0`}>{s.labelJa}</span>
                    <span className="text-zinc-500 tabular-nums shrink-0">{fmtDT(h.started_at)}</span>
                    <span className="text-zinc-400 truncate flex-1">
                      クロール / Crawl · 索引 {h.stats?.indexed || 0} / スキップ {h.stats?.skipped || 0} / 失敗 {h.stats?.failed || 0}
                    </span>
                    <Button size="sm" variant="outline" className="h-6 text-[10.5px] shrink-0"
                      onClick={(e) => openDetail({ kind: 'crawl', id: h.id, context: 'historical_crawl' }, e)}
                      data-testid={`nas-history-details-${i}`}>明細を見る</Button>
                  </div>
                );
              })}
            </div>
          </div>
          {previewHistory.length > 0 && (
            <div>
              <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">
                過去のプレビュー <span className="text-[10.5px] font-normal text-zinc-400">Historical previews</span>
              </p>
              <div className="divide-y divide-zinc-200 border border-zinc-200 rounded text-[11px]"
                data-testid="nas-preview-history">
                {previewHistory.map((h, i) => {
                  const s = derivePreviewState(h);
                  return (
                    <div key={h.job_id} className="px-3 py-1.5 flex items-center gap-2"
                      data-testid={`nas-preview-history-row-${i}`} data-state={s.key}>
                      <s.Icon size={12} className={s.color} />
                      <span className={`font-semibold ${s.color} shrink-0`}>{s.labelJa}</span>
                      <span className="text-zinc-500 tabular-nums shrink-0">{fmtDT(h.started_at)}</span>
                      <span className="text-zinc-400 truncate flex-1">
                        {h.preview_mode === 'full_validation' ? '完全検証 / Full' : '簡易見積り / Estimate'}
                        {' · '}対象 {h.aggregate?.eligible_files || 0} / エラー {h.aggregate?.failures || 0}
                        {h.pagination_used ? ` · ${h.aggregate?.pages || 0} ページ` : ''}
                        {h.truncated ? ' · 不完全' : ''}
                      </span>
                      <Button size="sm" variant="outline" className="h-6 text-[10.5px] shrink-0"
                        onClick={(e) => openDetail({ kind: 'preview', id: h.job_id,
                          context: h.is_active ? 'current_preview' : 'historical_preview' }, e)}
                        data-testid={`nas-preview-history-details-${i}`}>明細を見る</Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <NasRunDetailSheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}
        target={detail} returnFocusRef={detailTriggerRef}
        onCancelled={() => refreshPreview()} />

      <NasIngestDetailSheet open={!!ingestDetailId}
        onOpenChange={(o) => { if (!o) setIngestDetailId(null); }}
        jobId={ingestDetailId} returnFocusRef={ingestTriggerRef} />
    </div>
  );
}


// ── Search index stats + quick search ─────────────────────────────────
function SearchStatsCard({ stats, onRefresh }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);

  const perModuleRows = useMemo(() => {
    if (!stats) return [];
    const known = { ...(stats.known_modules || {}), nas: 'NAS ドキュメント' };
    return Object.keys(known).map((k) => ({ module: k, label: known[k], count: stats.per_module?.[k] || 0 }));
  }, [stats]);

  const search = async () => {
    const q = query.trim();
    if (!q) { setHits([]); return; }
    setSearching(true);
    try {
      const r = await axios.post(`${API}/api/crawler/search`, { query: q, limit: 10 }, { headers: authHeader() });
      setHits(r.data.hits || []);
    } catch (e) { toast.error(e?.response?.data?.detail || '検索に失敗しました'); }
    finally { setSearching(false); }
  };

  if (!stats) return null;
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="ai-learning-stats-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-700 flex items-center justify-center shrink-0">
          <Brain size={18} weight="duotone" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-zinc-900">統合検索インデックス</h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">
            合計 <span className="font-semibold text-zinc-800 tabular-nums">{(stats.total || 0).toLocaleString()}</span> 件がAIの検索対象です
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="h-8 text-[12px]" data-testid="stats-refresh"><ArrowsClockwise size={13} className="mr-1" />更新</Button>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="stats-grid">
          {perModuleRows.map((r) => (
            <div key={r.module} className="rounded-md border border-zinc-200 px-2.5 py-1.5">
              <p className="text-[11px] text-zinc-500 truncate">{r.label}</p>
              <p className="text-[15px] font-bold text-zinc-900 tabular-nums">{r.count.toLocaleString()}</p>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 px-3 py-2.5">
          <p className="text-[12px] font-semibold text-zinc-700 mb-1.5">動作確認: 統合検索</p>
          <div className="flex gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="キーワードで全モジュール検索…"
              className="h-9 text-[13px]" data-testid="stats-search-input" />
            <Button onClick={search} disabled={searching || !query.trim()}
              className="h-9 bg-zinc-800 hover:bg-zinc-900 text-[13px]" data-testid="stats-search-btn">
              <MagnifyingGlass size={13} className="mr-1" /> 検索
            </Button>
          </div>
          {hits.length > 0 && (
            <div className="mt-2 space-y-1.5" data-testid="stats-search-results">
              {hits.map((h) => (
                <div key={h.source_key} className="rounded border border-zinc-200 bg-white px-3 py-2 text-[12px]">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">
                      {perModuleRows.find((r) => r.module === h.module)?.label || h.module}
                    </span>
                    <p className="font-semibold text-zinc-800 truncate">{h.title}</p>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{h.body_text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Nightly schedule card ─────────────────────────────────────────────
function ScheduleCard({ onRunNow }) {
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firing, setFiring] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/crawler/schedule/config`, { headers: authHeader() });
      setCfg(r.data); setDirty(false);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => { setCfg((c) => ({ ...c, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/crawler/schedule/config`, {
        enabled: cfg.enabled, hour_jst: cfg.hour_jst, minute_jst: cfg.minute_jst,
        include_app_data: cfg.include_app_data, include_nas: cfg.include_nas,
      }, { headers: authHeader() });
      toast.success('スケジュールを保存しました');
      setDirty(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setFiring(true);
    try {
      await axios.post(`${API}/api/crawler/schedule/run-now`, {}, { headers: authHeader() });
      toast.success('スケジュールジョブを起動しました');
      onRunNow?.();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '実行に失敗しました'); }
    finally { setFiring(false); }
  };

  if (!cfg) return null;
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="ai-schedule-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-700 flex items-center justify-center shrink-0">
          <ArrowsClockwise size={18} weight="duotone" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-zinc-900">自動クロール（毎日）</h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">指定した時刻に両方のクローラーを自動起動します。取りこぼしを毎晩修復します。</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="ai-schedule-enabled-toggle">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} className="w-4 h-4 accent-indigo-600" />
          <span className={`text-[12px] font-semibold ${cfg.enabled ? 'text-emerald-700' : 'text-zinc-400'}`}>
            {cfg.enabled ? '有効' : '無効'}
          </span>
        </label>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-zinc-700 tracking-wider">起動時刻 (JST)</label>
            <div className="mt-1 flex items-center gap-2">
              <select value={cfg.hour_jst} onChange={(e) => set('hour_jst', Number(e.target.value))}
                disabled={!cfg.enabled}
                className="h-9 border border-zinc-200 rounded px-2 text-[13px] tabular-nums bg-white"
                data-testid="ai-schedule-hour">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}</option>)}
              </select>
              <span className="text-zinc-500">:</span>
              <select value={cfg.minute_jst} onChange={(e) => set('minute_jst', Number(e.target.value))}
                disabled={!cfg.enabled}
                className="h-9 border border-zinc-200 rounded px-2 text-[13px] tabular-nums bg-white"
                data-testid="ai-schedule-minute">
                {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
              </select>
              <span className="text-[11px] text-zinc-400">← ±30分の窓内で1日1回実行</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-zinc-700 tracking-wider">対象</label>
            <div className="mt-1 flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                <input type="checkbox" checked={cfg.include_app_data} onChange={(e) => set('include_app_data', e.target.checked)}
                  disabled={!cfg.enabled} className="w-4 h-4 accent-violet-600" data-testid="ai-schedule-appdata" />
                <span>Web アプリのデータ</span>
              </label>
              <label className="flex items-center gap-2 text-[12px] cursor-pointer">
                <input type="checkbox" checked={cfg.include_nas} onChange={(e) => set('include_nas', e.target.checked)}
                  disabled={!cfg.enabled} className="w-4 h-4 accent-amber-600" data-testid="ai-schedule-nas" />
                <span>NAS ドキュメント</span>
              </label>
            </div>
          </div>
        </div>

        {cfg.last_run_at && (
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px]" data-testid="ai-schedule-last-run">
            <p className="font-semibold text-zinc-600">前回実行: {fmtDT(cfg.last_run_at)}</p>
            {cfg.last_run_result && (
              <div className="text-zinc-500 mt-0.5">
                {cfg.last_run_result.app_data && <div>Web: {cfg.last_run_result.app_data}</div>}
                {cfg.last_run_result.nas && <div>NAS: {cfg.last_run_result.nas}</div>}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button onClick={runNow} disabled={firing}
            variant="outline" className="h-9 text-[13px]" data-testid="ai-schedule-run-now">
            <Play size={13} weight="fill" className="mr-1" /> {firing ? '起動中…' : '今すぐ実行'}
          </Button>
          <Button onClick={save} disabled={saving || !dirty}
            className="h-9 bg-teal-700 hover:bg-teal-800 text-[13px]" data-testid="ai-schedule-save">
            <FloppyDisk size={13} className="mr-1" /> {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── Semantic search (embeddings) card ─────────────────────────────────
function EmbeddingsCard() {
  const [cfg, setCfg] = useState(null);
  const [job, setJob] = useState({ running: false });
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [c, j] = await Promise.all([
        axios.get(`${API}/api/crawler/embeddings/config`, { headers: authHeader() }),
        axios.get(`${API}/api/crawler/embeddings/backfill`, { headers: authHeader() }),
      ]);
      setCfg(c.data); setJob(j.data || { running: false });
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (job?.running) {
      pollRef.current = setInterval(async () => {
        try {
          const [c, j] = await Promise.all([
            axios.get(`${API}/api/crawler/embeddings/config`, { headers: authHeader() }),
            axios.get(`${API}/api/crawler/embeddings/backfill`, { headers: authHeader() }),
          ]);
          setCfg(c.data); setJob(j.data);
          if (!j.data?.running) clearInterval(pollRef.current);
        } catch { /* */ }
      }, 2500);
      return () => clearInterval(pollRef.current);
    }
  }, [job?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const setEnabled = async (on) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/crawler/embeddings/config`, { enabled: on }, { headers: authHeader() });
      setCfg(r.data);
      toast.success(on ? '意味検索を有効にしました' : '意味検索を無効にしました');
    } catch (e) { toast.error(e?.response?.data?.detail || '設定に失敗しました'); }
    finally { setSaving(false); }
  };

  const startBackfill = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/api/crawler/embeddings/backfill`, {}, { headers: authHeader() });
      toast.success('埋め込み計算を開始しました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '実行に失敗しました'); }
    finally { setStarting(false); }
  };

  if (!cfg) return null;
  const coverage = cfg.total ? Math.round((cfg.with_embedding / cfg.total) * 100) : 0;
  const backfillPct = job?.total ? Math.round((job.processed / job.total) * 100) : 0;
  const missing = Math.max(0, (cfg.total || 0) - (cfg.with_embedding || 0));

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="ai-embeddings-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-700 flex items-center justify-center shrink-0">
          <Brain size={18} weight="duotone" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-zinc-900">意味検索（Embeddings）</h3>
          <p className="text-[12px] text-zinc-500 mt-0.5">
            「止まってる」で「停止」も見つかる、意味の近い文書を返します（多言語対応・完全ローカル・384次元）
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="ai-embeddings-toggle">
          <input type="checkbox" checked={cfg.enabled} disabled={saving}
            onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-fuchsia-600" />
          <span className={`text-[12px] font-semibold ${cfg.enabled ? 'text-emerald-700' : 'text-zinc-400'}`}>
            {cfg.enabled ? '有効' : '無効'}
          </span>
        </label>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded border border-zinc-200 px-2.5 py-1.5">
            <p className="text-[11px] text-zinc-500">対象文書</p>
            <p className="text-[15px] font-bold text-zinc-900 tabular-nums">{(cfg.total || 0).toLocaleString()}</p>
          </div>
          <div className="rounded border border-zinc-200 px-2.5 py-1.5">
            <p className="text-[11px] text-zinc-500">埋め込み済み</p>
            <p className="text-[15px] font-bold text-fuchsia-700 tabular-nums" data-testid="embeddings-with">{(cfg.with_embedding || 0).toLocaleString()}</p>
          </div>
          <div className="rounded border border-zinc-200 px-2.5 py-1.5">
            <p className="text-[11px] text-zinc-500">カバー率</p>
            <p className="text-[15px] font-bold text-zinc-900 tabular-nums">{coverage}%</p>
          </div>
        </div>
        <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
          <div className="h-full bg-teal-700 transition-all" style={{ width: `${coverage}%` }} />
        </div>

        {job?.running && (
          <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-3 py-2.5" data-testid="embeddings-backfill-progress">
            <p className="text-[12px] font-semibold text-fuchsia-900 flex items-center gap-2">
              <CircleNotch size={14} className="animate-spin" />
              埋め込みを計算中… {job.processed} / {job.total} ({backfillPct}%)
            </p>
            <div className="mt-2 h-1.5 rounded-full bg-white/70 overflow-hidden">
              <div className="h-full bg-fuchsia-500 transition-all" style={{ width: `${backfillPct}%` }} />
            </div>
          </div>
        )}

        {!cfg.enabled && (
          <p className="text-[11px] text-zinc-500">
            トグルを有効にすると、多言語埋め込みモデル（220MB・初回のみダウンロード）を使って意味検索が有効化されます。
          </p>
        )}

        <div className="flex gap-2 justify-end">
          <Button onClick={startBackfill} disabled={starting || !cfg.enabled || job?.running || missing === 0}
            className="h-9 bg-teal-700 hover:bg-teal-800 text-[13px]" data-testid="embeddings-backfill-btn">
            <ArrowsClockwise size={13} className="mr-1" />
            {missing === 0 ? 'すべて埋め込み済み' :
              starting ? '起動中…' : `未処理 ${missing.toLocaleString()} 件を計算`}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── Page ──────────────────────────────────────────────────────────────
export default function AiLearningSources() {
  const [stats, setStats] = useState(null);

  const loadStats = async () => {
    try {
      const s = await axios.get(`${API}/api/crawler/search-stats`, { headers: authHeader() });
      setStats(s.data);
    } catch { /* silent */ }
  };
  useEffect(() => { loadStats(); }, []);

  return (
    <div className="space-y-5" data-testid="ai-learning-page">
      <div className="bg-zinc-50 border border-violet-200 rounded-xl px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-700 flex items-center justify-center shrink-0">
            <Brain size={20} weight="duotone" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-zinc-900 text-[17px]">AI 学習ソース</h1>
            <p className="text-[12px] text-zinc-600 mt-0.5">
              アプリ内のデータと NAS 上のドキュメントを横断的にインデックス化し、AI アシスタントが全社の情報を検索できるようにします。
              長時間のクロールはいつでも一時停止・再開が可能です。
            </p>
          </div>
        </div>
      </div>

      <AppDataCrawler onStatsChanged={loadStats} />
      <NasCrawler onStatsChanged={loadStats} />
      <EvidenceRouterCard />
      <ScheduleCard onRunNow={loadStats} />
      <EmbeddingsCard />
      <SearchStatsCard stats={stats} onRefresh={loadStats} />
    </div>
  );
}
